import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CompanyFilter } from '../components/CompanyFilter';
import { useDateRange } from '../hooks/useDateRange';
import { DateRangeFilter } from '../components/DateRangeFilter';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Download } from 'lucide-react';

export default function SupplierComparisonPage() {
  const [data, setData] = useState({ suppliers: [] });
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [sites, setSites] = useState([]);
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterSite, setFilterSite] = useState('all');
  const { dateFrom, dateTo, setDateFrom, setDateTo, dateParams } = useDateRange();

  const fetchData = () => {
    const params = {};
    if (filterCompany !== 'all') params.company_id = filterCompany;
    if (filterSite !== 'all') params.site_id = filterSite;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get(`/analytics/supplier-comparison?${new URLSearchParams(params)}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get('/companies').then(r => setCompanies(r.data)).catch(() => {});
    api.get('/sites').then(r => setSites(r.data)).catch(() => {});
    fetchData();
  }, []);

  useEffect(() => { setLoading(true); fetchData(); }, [filterCompany, filterSite, dateFrom, dateTo]);

  const chartData = (data.suppliers || []).filter(s => s.cost_per_hour > 0).slice(0, 8).map(s => ({ name: s.name?.substring(0, 14), cph: s.cost_per_hour, spend: s.total_spend }));

  return (
    <div data-testid="supplier-comparison-page">
      <div className="page-header">
        <div><h1 className="page-title">Supplier Comparison</h1><p className="text-sm text-slate-500 mt-1">Compare suppliers by spend, performance, and reliability</p></div>
      </div>

      <div className="flex gap-3 mb-6 flex-wrap items-end" data-testid="supplier-filters">
        <CompanyFilter companies={companies} value={filterCompany} onChange={setFilterCompany} />
        <Select value={filterSite} onValueChange={setFilterSite}><SelectTrigger className="w-48 bg-white"><SelectValue placeholder="All Sites" /></SelectTrigger><SelectContent><SelectItem value="all">All Sites</SelectItem>{sites.map(s => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}</SelectContent></Select>
        <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} prefix="supplier" />
      </div>

      {chartData.length > 0 && (
        <Card className="bg-white border border-slate-200 rounded-lg p-5 mb-6">
          <h3 className="text-sm font-semibold text-slate-900 font-heading mb-4">Cost Per Hour by Supplier</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748B' }} /><YAxis tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={v => `₹${v}`} /><Tooltip formatter={v => `₹${Number(v).toFixed(2)}`} contentStyle={{ border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 12 }} /><Bar dataKey="cph" fill="#0F172A" radius={[4,4,0,0]} name="Cost/Hr" /></BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      <div className="data-table-wrapper">
        <Table>
          <TableHeader><TableRow className="bg-slate-50 border-b border-slate-200">
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Supplier</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Spend</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Qty</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hours</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cost/Hr</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cost/100Hr</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Avg Life</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Products</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Trials</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Conf.</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow> :
            (data.suppliers || []).length === 0 ? <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-400">No supplier data</TableCell></TableRow> :
            (data.suppliers || []).map((s, i) => (
              <TableRow key={s.supplier_id} className={`hover:bg-slate-50/50 ${i === 0 && s.cost_per_hour > 0 ? 'bg-emerald-50/30' : ''}`}>
                <TableCell className="font-medium text-slate-900">{s.name}</TableCell>
                <TableCell className="text-sm font-medium">₹{s.total_spend.toLocaleString()}</TableCell>
                <TableCell className="text-sm text-slate-600">{s.total_qty}</TableCell>
                <TableCell className="text-sm text-slate-600">{s.total_hours.toLocaleString()}h</TableCell>
                <TableCell className="text-sm font-bold text-slate-900">{s.cost_per_hour > 0 ? `₹${s.cost_per_hour.toFixed(2)}` : '-'}</TableCell>
                <TableCell className="text-sm">{s.cost_per_100hrs > 0 ? `₹${s.cost_per_100hrs.toFixed(2)}` : '-'}</TableCell>
                <TableCell className="text-sm text-slate-600">{s.avg_life_hours > 0 ? `${s.avg_life_hours.toFixed(0)}h` : '-'}</TableCell>
                <TableCell className="text-sm text-slate-600">{s.product_count}</TableCell>
                <TableCell className="text-sm text-slate-600">{s.trial_count}</TableCell>
                <TableCell><span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${s.confidence === 'high' ? 'bg-emerald-50 text-emerald-600' : s.confidence === 'medium' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-400'}`}>{s.confidence}</span></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
